import {produce} from 'immer';
import {AttrsSchema, Component} from 'panel';
import partition from 'lodash/partition';
import moment from 'moment';

import {capitalize, pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Visibility} from 'common/components/base/stackable';
import {apiClient} from 'common/util/api-client';
import {getServiceAccountNameErrorMessage} from 'common/util/service-accounts';
import {reporter} from 'common/report/error-reporting';
import {
  addOrgUsers,
  addOrgUsersWithAttachment,
  addUsersToOrgProjects,
  addUsersToOrgTeams,
  fetchDomainData,
  fetchOrgProjectData,
  fetchOrgTeamData,
  fetchOrgUserData,
  getOrgAssignableOrgRoles,
  getOrgAssignableProjectRoles,
} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {abbreviateStrings, isValidEmailAddress, isVerifiedDomain} from 'common/util/string';
import {CreateServiceAccountResult} from 'app_api/organizations/service_accounts/types';
import {ExpiresUnit} from 'common/types/reports/service-accounts';
import {DetailedUser} from 'common/report/queries/app-api/legacy/org-types';
import {IronContextRegistry} from 'common/report/context-registry';
import {assertUnreachable} from 'common/util/typescript';

import {template} from './template';
import css from './index.inline.styl';
import {
  InviteUsersModalAttrsSchema,
  InviteUsersModalJsxProps,
  InviteUsersModalState,
  PartialProject,
  Tab,
  UserRow,
  UserType,
} from './types';

const ORG_ROLE_MEMBER = `member`;

let currUserRowId = 0;

export function usersToProjectsAndTeams(users): any {
  const projects = {};
  const teams = {};

  users.forEach((u) => {
    (u.projectIds ?? []).forEach((userProject) => {
      if (projects[userProject]) {
        projects[userProject].push({email: u.email, role: u.projectRole});
      } else {
        projects[userProject] = [{email: u.email, role: u.projectRole}];
      }
    });
    (u.teamNames ?? []).forEach((teamName) => {
      if (teams[teamName]) {
        teams[teamName].push(u.email);
      } else {
        teams[teamName] = [u.email];
      }
    });
  });

  return {
    projects: Object.entries(projects).map(([id, users]) => ({
      id: parseInt(id, 10),
      users,
    })),
    teams: Object.entries(teams).map(([name, userEmails]) => ({
      name,
      userEmails,
    })),
  };
}

export class InviteUsersModal extends Component<
  InviteUsersModalState,
  InviteUsersModalAttrsSchema,
  any,
  any,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InviteUsersModalAttrsSchema> {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`, description: `Should the modal be open?`},
      'exclude-teams': {type: `boolean`},
      'user-type': {type: `string`, default: UserType.User, enum: Object.values(UserType)},
      type: {type: `string`, default: Tab.Project},
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.isServiceAccount()) {
      // default org role and project role
      const updatedUserRows = this.state.userRows.map((row) => {
        return {
          ...row,
          orgRole: `member`,
          projectRole: `consumer`,
        };
      });
      this.update({userRows: updatedUserRows});
    }
  }

  getBlankUserRow(): UserRow {
    return {
      id: ++currUserRowId,
      email: null,
      ...this.getOrgSwitchPartialUserRow(),
    };
  }

  getOrgSwitchPartialUserRow(): Omit<UserRow, 'id' | 'email'> {
    const singleProjectId = this.state ? this.getProjectIdIfSingleProject() : null;

    return {
      orgRole: `member`,
      projectIds: singleProjectId ? [singleProjectId] : [],
      projectRole: null,
      teamNames: [],
      orgRoleError: false,
      projectError: false,
      projectRoleError: false,
      emailError: false,
      emailErrorMsg: null,
      emailInfo: false,
      projectIdsToDowngrade: [],
      seeAll: false,
      error: null,
    };
  }

  getCurrentOrgProjectData() {
    return this.state.projectRoles?.projects || {};
  }

  get config() {
    return {
      contexts: [`currentProject` as const],
      css,
      template,
      useShadowDom: true,

      defaultState: {
        isLoading: false,
        isOrgProjectsProp: false,
        org: null,
        orgProjects: null,
        orgDomains: null,
        projectRoles: null,
        orgRoles: null,
        orgTeams: null,
        orgUsers: null,
        project: null,
        projectMembers: null,
        serviceAccount: null,
        userFile: {},
        userRows: [this.getBlankUserRow()],
      },

      helpers: {
        // after copying username and secret for service accounts
        handleClickedDone: () => {
          if (this.isServiceAccount()) {
            this.dispatchEvent(
              new CustomEvent(`notify`, {
                detail: {
                  message: `Service account created successfully`,
                },
              }),
            );
          }
          this.dispatchEvent(new CustomEvent(`done`));
        },

        // email input
        updateUserEmail: (ev, row) => {
          this.updateUserRow(row, {
            email: ev.target.value,
          });
        },

        // name input
        updateServiceAccountName: (ev, row) => {
          const name = ev.target.value;
          const nameErrorMsg = getServiceAccountNameErrorMessage(name);
          const nameError = !!nameErrorMsg;
          this.updateUserRow(row, {
            name,
            nameError,
            nameErrorMsg,
          });
        },
        hasInvalidName: () => {
          // we only create one service account at a time
          const row = this.state.userRows[0];
          return row.name === null || !!getServiceAccountNameErrorMessage(row.name);
        },
        serviceAccountNameValidationStatus: (row) => {
          return !row.name && row.name !== `` ? `` : row.nameError ? `invalid` : `valid`;
        },
        // org role input
        getOrgRoleSections: () => [
          {
            items: this.state.orgRoles?.map((orgRole) => ({
              ...orgRole,
              label: capitalize(orgRole.name),
              subLabel: orgRole.description,
              wrapSubLabel: true,
            })),
          },
        ],
        selectOrgRole: (ev, row) => {
          this.updateUserRow(row, {
            orgRole: ev.detail.item.name,
          });
        },

        // project input
        getProjectSections: (row) => [
          {
            items: this.state.orgProjects
              .filter((project) => this.canInviteToProject(project))
              .map((project) => ({
                ...project,
                label: project.name,
                isSelected: row.projectIds.includes(project.id),
              })),
          },
        ],
        toggleProjectSelected: (ev, row) => {
          const id = ev.detail.item.id;
          const pids = row.projectIds;
          const projectIds = pids.includes(id) ? pids.filter((pid) => pid !== id) : pids.concat(id);
          this.updateUserRow(row, {
            projectIds,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, null, projectIds),
          });
        },
        toggleAllProjectsSelected: (ev, row) => {
          const projects = this.state.orgProjects.filter((project) => this.canInviteToProject(project));
          const projectIds = ev.detail.selectionState === `all` ? [] : projects.map((p) => p.id);
          this.updateUserRow(row, {
            projectIds,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, null, projectIds),
          });
        },
        // project role input
        getProjectRoleSections: () => {
          const roleMap = {};

          Object.values(this.getCurrentOrgProjectData()).forEach((roleArray: any) =>
            roleArray
              // deduplicate
              .forEach((role) => (roleMap[role.name] = role)),
          );

          return [
            {
              items: Object.values(roleMap).map((pr: any) => ({
                ...pr,
                label: capitalize(pr.name),
                subLabel: pr.description,
                wrapSubLabel: true,
              })),
            },
          ];
        },
        getProjectIdsToDowngrade: (row, roleName = null, projectIds = null) => {
          if (!roleName && !row.projectRole) {
            return [];
          }
          return (projectIds || row.projectIds).filter((projectId) =>
            this.getCurrentOrgProjectData()[projectId].every((role) => role.name !== (roleName || row.projectRole)),
          );
        },

        selectProjectRole: (ev, row) => {
          const roleName = ev.detail.item.name;
          this.updateUserRow(row, {
            projectRole: roleName,
            projectIdsToDowngrade: this.helpers.getProjectIdsToDowngrade(row, roleName),
          });
        },

        // team input
        // TODO: should really only show teams that the user has perms to invite to
        getTeamSections: (row) => [
          {
            items: this.state.orgTeams.map((team) => ({
              ...team,
              label: team.name,
              isSelected: row.teamNames.includes(team.name),
            })),
          },
        ],
        toggleTeamSelected: (ev, row) => {
          const name = ev.detail.item.name;
          const teams = row.teamNames;
          this.updateUserRow(row, {
            teamNames: teams.includes(name) ? teams.filter((t) => t !== name) : teams.concat(name),
          });
        },
        toggleAllTeamsSelected: (ev, row) => {
          const teams = this.state.orgTeams;
          this.updateUserRow(row, {
            teamNames: ev.detail.selectionState === `all` ? [] : teams.map((t) => t.name),
          });
        },

        // error management
        updateEmailError: (ev, row) => {
          this.updateUserRow(
            row,
            this.validateEmail({
              ...row,
              email: ev.target.value,
            }),
          );
        },
        clearErrors: () => {
          this.update({
            userRows: this.state.userRows.map((row) => ({
              ...row,
              orgRoleError: false,
              projectError: false,
              projectRoleError: false,
              emailError: false,
              emailErrorMsg: row.emailInfo ? row.emailErrorMsg : null,
              nameError: false,
              nameErrorMsg: null,
            })),
          });
        },

        // button click handlers
        clickedAddMoreUser: () => {
          this.update({
            userRows: [...this.state.userRows, this.getBlankUserRow()],
          });
        },
        clickedDeleteButton: (row) => {
          this.update({
            userRows: this.state.userRows.filter((r) => r.id !== row.id),
          });
        },
        clickedSave: async () => {
          if (this.isServiceAccount()) {
            this.update(this.validateCreateServiceAccountForm());
            if (this.allRowsValid()) {
              this.update({isLoading: true});
              await this.createServiceAccount(this.state.userRows);
            }
          } else {
            switch (this.inviteType) {
              case Tab.Org:
              case Tab.Team: {
                this.update(this.validateAddUsersForm());
                if (this.allRowsValid()) {
                  this.update({isLoading: true});
                  await this.sendInvitation(this.state.userRows);
                }
                break;
              }
              case Tab.Bulk: {
                this.update(this.validateBulkAddForm());
                if (!this.state.userFile.error) {
                  this.update({isLoading: true});
                  await this.sendCsvFile(this.state.userFile.csvFile);
                }
                break;
              }
            }
          }

          this.update({isLoading: false});
        },
        clickedCancel: () => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },

        // status change listeners
        fileStatusChanged: (ev) => {
          if (ev.detail && ev.detail.file) {
            const file = ev.detail.file;
            this.update({userFile: {name: file.name, csvFile: file}});
          }
        },
        removeSelectedFile: () => {
          this.update({userFile: {}});
        },
        modalVisibilityChanged: (ev) => {
          if (ev.detail && ev.detail.state === Visibility.Closed) {
            // once modal close animation is complete, reset UI state
            const {userRows, userFile} = this.config.defaultState;
            this.update({userRows, userFile});
          } else {
            // sync data on modal open
            this.syncData();
          }
        },
        trackUpsellClick: (ev) => {
          ev.preventDefault();
          track(
            events.clickUpsell,
            {[properties.featureLimit]: properties.featureLimitTeamMembers},
            () => (window.location.href = ev.target.href),
          );
        },
        getDowngradedProjectsString: (row) => {
          return abbreviateStrings(
            row.projectIdsToDowngrade.map((pid) => this.state.orgProjects.find((proj) => proj.id === pid).name),
            140,
          );
        },
        getAllDowngradedProjectsString: (row) =>
          row.projectIdsToDowngrade
            .map((pid) => this.state.orgProjects.find((proj) => proj.id === pid).name)
            .join(`, `),
        seeAllDowngradedProjects: (rowIdx) =>
          this.update({
            userRows: produce(this.state.userRows, (userRows) => {
              userRows[rowIdx].seeAll = true;
            }),
          }),

        selectExpirationMode: (ev, row) => {
          if (ev.detail.value === `Specify...`) {
            this.updateUserRow(row, {
              expires: {
                interval: 30,
                unit: ExpiresUnit.Day,
              },
            });
          }
        },
        selectExpiresUnit: (ev, row) => {
          const expires = {...(row.expires || {}), unit: ev.detail.value};
          this.updateUserRow(row, {
            expires,
          });
        },
        updateExpiresInterval: (ev, row) => {
          const expires = {...(row.expires || {}), interval: parseInt(ev.target.value)};
          this.updateUserRow(row, {
            expires,
          });
        },
      },
    };
  }

  set orgUsers(orgUsers: DetailedUser[]) {
    this.update({orgUsers});
  }

  set orgProjects(orgProjects: PartialProject[]) {
    this.update({orgProjects, isOrgProjectsProp: true});

    // Special case where if `orgProjects` is passed in and there is a single project,
    // we explicitly don't render a project selection. This is to streamline the invite flow
    // in the context of a specific project, rather than at an organization level.
    const defaultProjectId = this.getProjectIdIfSingleProject();
    if (defaultProjectId) {
      this.update({
        userRows: this.state.userRows.map((row) => ({...row, projectIds: [defaultProjectId]})),
      });
    }
  }

  set orgTeams(orgTeams) {
    this.update({orgTeams});
  }

  set org(org) {
    this.update({
      org,
    });
  }

  set project(project) {
    this.update({
      project,
    });
  }

  isOverSeatCount() {
    const org = this.state.org;
    const users = org && this.state.orgUsers;
    return users && org[`seat_count`] < users.length + this.state.userRows.length;
  }

  getProjectIdIfSingleProject(): number | null {
    if (!this.state.isOrgProjectsProp) {
      return null;
    }

    return this.state.orgProjects.length === 1 ? this.state.orgProjects[0].id : null;
  }

  syncData({refetch = []} = {}) {
    const {org, project} = this.state;

    const syncFields: Array<
      [stateKey: keyof InviteUsersModalState, fetchData: () => Promise<any>, disabled?: () => boolean]
    > = [
      [`orgUsers`, () => fetchOrgUserData(org.id)],
      [`orgRoles`, () => getOrgAssignableOrgRoles(org.id)],
      [`orgProjects`, () => fetchOrgProjectData(org.id)],
      [`projectRoles`, () => getOrgAssignableProjectRoles(org.id)],
      [`orgDomains`, () => fetchDomainData(org.id)],
      [`orgTeams`, () => fetchOrgTeamData(org.id), () => !!this.attr(`exclude-teams`)],
      [
        `projectMembers`,
        () => apiClient.get(`/api/app/projects/${project.id}/granted-users`),
        () => this.inviteType !== Tab.Project,
      ],
    ];

    // wait until a single org is specified to fetch data
    if (org) {
      syncFields
        .filter(
          ([stateKey, _fetchData, disabled]) =>
            // don't overwrite existing data unless explicitly requested via `refetch` arg
            (disabled === undefined || !disabled()) && (!this.state[stateKey] || refetch.includes(stateKey)),
        )
        .map(([stateKey, fetchData, _disabled]) => {
          // fetch data asynchronously with `then` instead of `async/await`
          // so that it appears in state incrementally as it becomes available
          return fetchData().then((data) =>
            this.update({
              [stateKey]: data,
            }),
          );
        });
    }
  }

  updateUserRow(row: UserRow, data: Partial<UserRow>) {
    this.update({
      userRows: this.state.userRows.map((r) => (r.id !== row.id ? r : {...row, ...data})),
    });
  }

  getTitle() {
    if (this.isServiceAccount()) {
      return `Add Service Account`;
    }

    switch (this.inviteType) {
      case Tab.Project:
        return `Invite to ${this.currentProject()?.name ?? `current project`}`;
      case Tab.Org:
        return `Invite to ${this.state.org.name || this.appState.org.name}`;
      case Tab.Team:
        return `Invite to Teams at ${this.state.org.name}`;
      case Tab.Bulk:
        return `Bulk Invite to ${this.state.org.name}`;
      default:
        assertUnreachable(this.inviteType);
    }
  }

  allRowsValid() {
    const {userRows} = this.state;
    if (this.isServiceAccount()) {
      return userRows.every((row) => !(row.nameError || row.orgRoleError || row.projectError || row.projectRoleError));
    } else {
      return userRows.every((row) => !(row.emailError || row.orgRoleError || row.projectError || row.projectRoleError));
    }
  }

  currentProject() {
    return this.getContext(`currentProject`).get()?.value;
  }

  validateEmail(row) {
    // don't show validation errors for empty email
    if (row.email && row.email.length) {
      const verifiedDomain = isVerifiedDomain(row.email, this.state.orgDomains);
      const invalidEmail = !isValidEmailAddress(row.email);

      row = {
        ...row,
        emailError: invalidEmail,
        emailInfo: !verifiedDomain && !invalidEmail,
        emailErrorMsg: invalidEmail ? null : !verifiedDomain ? `This email is not a part of your organization` : null,
      };
    }
    return row;
  }

  validateName(row) {
    if (row.name && row.name.length) {
      const nameErrorMsg = getServiceAccountNameErrorMessage(row.name);
      row = {
        ...row,
        nameError: !!nameErrorMsg,
        nameErrorMsg,
      };
    }
    return row;
  }

  validateOrgRole(row) {
    return {
      ...row,
      orgRoleError: !row.orgRole,
    };
  }

  get inviteType() {
    return this.attr(`type`);
  }

  validateProject(row) {
    return {
      ...row,
      projectError: this.inviteType === Tab.Org && !row.projectIds.length,
    };
  }

  validateProjectRole(row) {
    if (this.isServiceAccount()) {
      return {
        ...row,
        projectRoleError: !row.projectRole && row.projectIds.length,
      };
    }
    return {
      ...row,
      projectRoleError: this.inviteType === Tab.Org && !row.projectRole,
    };
  }

  validateCreateServiceAccountForm() {
    return {
      userRows: this.state.userRows
        .map((row) => this.validateName(row))
        .map((row) => this.validateOrgRole(row))
        .map((row) => this.validateProjectRole(row))
        .map((row) => ({
          ...row, // only validate name length on form submit
          nameError: row.nameError || !(row.name && row.name.length),
        })),
    };
  }

  validateAddUsersForm() {
    return {
      userRows: this.state.userRows
        .map((row) => this.validateEmail(row))
        .map((row) => this.validateOrgRole(row))
        .map((row) => this.validateProject(row))
        .map((row) => this.validateProjectRole(row))
        .map((row) => ({
          ...row, // only validate email length on form submit
          emailError: row.emailError || !(row.email && row.email.length),
          emailInfo: row.emailInfo,
        })),
    };
  }

  validateBulkAddForm() {
    return {
      userFile: {
        ...this.state.userFile,
        error: !this.state.userFile.csvFile,
      },
    };
  }

  isServiceAccount() {
    return this.attr(`user-type`) === UserType.ServiceAccount;
  }

  async createServiceAccount(users) {
    // For now, we only create one service account at a time
    const serviceAccount = users[0];
    const projects = serviceAccount.projectIds.map((id) => ({id, role: serviceAccount.projectRole}));
    const expires =
      serviceAccount.expires && moment().add(serviceAccount.expires.unit, serviceAccount.expires.interval);

    const body: any = {
      username: serviceAccount.name,
      expires: expires && expires.toISOString().split(`.`)[0] + `Z`,
      projects,
    };
    // orgRole=member is equitable to no role specified
    if (serviceAccount.orgRole !== ORG_ROLE_MEMBER) {
      body.role = serviceAccount.orgRole;
    }

    try {
      const response = await apiClient.post<CreateServiceAccountResult>(
        `/api/app/organizations/${this.state.org.id}/service-accounts`,
        body,
      );
      this.update({
        serviceAccount: {
          secret: response.token,
          username: response.username,
        },
      });
      this.trackServiceAccountEvent(events.orgServiceAccountCreated, {successful: true});
    } catch (error) {
      const errorMsg = error.message;
      this.update({error: errorMsg});
    }
  }

  trackServiceAccountEvent(event, props = {}) {
    track(`${event}`, {
      screen: `Service Accounts`,
      ...props,
    });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async sendInvitation(users) {
    const orgUsersEmail = this.state.orgUsers.filter((ou) => ou.status === `Active`).map((ou) => ou.email);
    const [existingUsers, usersToInvite] = partition(users, (u) => orgUsersEmail.includes(u.email));

    // sanitize user data and transform project ids/role into project objects
    const userInvites = usersToInvite.map(
      ({email, name, orgRole, projectIds, projectIdsToDowngrade, projectRole, teamNames}) => ({
        email,
        name,
        teamNames,
        // TODO I don't really like that we are hard-coding admin here, but until custom roles are a thing it should work
        projects: projectIds.map((id) => ({id, role: projectIdsToDowngrade.includes(id) ? `admin` : projectRole})),
        // orgRole=member is equitable to no role specified
        ...(orgRole === ORG_ROLE_MEMBER ? {} : {orgRole}),
      }),
    );

    let isSuccessful;
    let successfulInvitations = [];
    let failedInvitations = [];

    try {
      await this.addExistingUsers(existingUsers);
      const rawResponse = await addOrgUsers(this.state.org.id, userInvites);
      isSuccessful = rawResponse.every((response) => typeof response.error === `undefined`);
      if (isSuccessful) {
        successfulInvitations = rawResponse;
      } else {
        failedInvitations = rawResponse.filter((response) => !response.invited);
      }
    } catch (error) {
      isSuccessful = false;
      let errorMsg;
      try {
        errorMsg = JSON.parse(error.message);
      } catch (jsonError) {
        errorMsg = [];
      }
      errorMsg.forEach((message) => {
        const response = message.split(`: `);
        if (response.length === 2) {
          const failureMessage = `Error inviting users: ${response[0]}`;
          const emails = response[1].split(`\n`);
          failedInvitations = failedInvitations.concat(
            emails.map((email) => ({
              email,
              error: failureMessage,
            })),
          );
        }
      });
      if (!failedInvitations.length) {
        this.dispatchEvent(
          new CustomEvent(`error`, {
            detail: {
              error,
              message: `Error inviting users: ${error.message}`,
            },
          }),
        );
      }
    }

    // success
    if (isSuccessful) {
      const numberOfEmails = successfulInvitations.filter((inv) => inv.invited).length;
      const invitationsText = pluralize(`invitation`, numberOfEmails);
      const hasText = pluralize(`has`, numberOfEmails, `have`);
      const invitationSentence =
        numberOfEmails > 0 ? `${numberOfEmails} email ${invitationsText} ${hasText} been sent. ` : ``;

      const numberAdded = successfulInvitations.filter((inv) => inv.added).length;
      const userText = pluralize(`user`, numberAdded);
      const addHasText = pluralize(`has`, numberAdded, `have`);
      const addedSentence =
        numberAdded > 0 ? `${numberAdded} ${userText} ${addHasText} been added to the organization.` : ``;

      if (invitationSentence || addedSentence) {
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: `${invitationSentence}${addedSentence}`,
            },
          }),
        );
      }

      track(events.inviteUser, {
        [properties.numUsers]: numberOfEmails,
        [properties.type]: `Single`,
        [properties.tab]: `Users`,
      });

      this.syncData({refetch: [`orgUsers`]});
      this.dispatchEvent(new CustomEvent(`save`));

      // failure
    } else {
      // keep modal open, prompt tooltip
      const userRows = this.state.userRows.map((row) => {
        const invitation = failedInvitations.find((invitation) => invitation.email === row.email);
        if (invitation) {
          return {...row, isValidEmail: false, emailErrorMsg: invitation.error, emailError: true, emailInfo: false};
        } else {
          return row;
        }
      });
      this.update({userRows});
    }
  }

  async addExistingUsers(users) {
    const userCount = new Set(users.map((u) => u.email)).size;
    const {projects, teams} = usersToProjectsAndTeams(users);
    if (projects.length > 0) {
      try {
        await addUsersToOrgProjects(this.state.org.id, projects);
        // addUsersToOrgTeams
        const userText = pluralize(`user`, userCount);
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: `Access updated for ${userCount} existing ${userText}.`,
            },
          }),
        );
      } catch (err) {
        reporter.error(err, `Failed updating existing user project grants`);
      }
    }
    if (teams.length > 0) {
      try {
        await addUsersToOrgTeams(
          this.state.org.id,
          teams.map(({name, userEmails}) => ({
            id: this.state.orgTeams.find((t) => t.name === name).id,
            userEmails,
          })),
        );
        // addUsersToOrgTeams
        const userText = pluralize(`user`, userCount);
        this.dispatchEvent(
          new CustomEvent(`notify`, {
            detail: {
              message: `Access updated for ${userCount} existing ${userText}.`,
            },
          }),
        );
      } catch (err) {
        reporter.error(err, `Failed updating existing user team grants`);
      }
    }
  }

  async sendCsvFile(attachment) {
    const formData = new FormData();
    formData.append(`csv`, attachment);

    let isSuccessful;
    let successfulInvitations = [];
    let failedInvitations = [];
    let errorMsg;

    try {
      const rawResponse = await addOrgUsersWithAttachment(this.state.org.id, formData);
      isSuccessful = rawResponse.every((response) => typeof response.error === `undefined`);
      if (isSuccessful) {
        successfulInvitations = rawResponse;
      } else {
        failedInvitations = rawResponse
          .filter((response) => !response.invited)
          .map((invitation) => `Can't invite ${invitation.email}, because ${invitation.error}`);
        errorMsg = failedInvitations.join(`. \n`);
      }
    } catch (error) {
      isSuccessful = false;
      errorMsg = error.message;
    }

    // success
    if (isSuccessful) {
      const numberOfEmails = successfulInvitations.length;
      const invitationsText = pluralize(`invitation`, numberOfEmails);
      const hasText = pluralize(`has`, numberOfEmails, `have`);

      this.dispatchEvent(
        new CustomEvent(`notify`, {
          detail: {
            message: `${numberOfEmails} email ${invitationsText} ${hasText} been sent.`,
          },
        }),
      );

      track(events.inviteUser, {
        [properties.numUsers]: numberOfEmails,
        [properties.type]: `Bulk`,
        [properties.tab]: `Users`,
        version: `v2`,
      });

      this.syncData({refetch: [`orgUsers`]});
      this.dispatchEvent(new CustomEvent(`save`));

      // failure
    } else {
      this.update({
        userFile: {
          ...this.state.userFile,
          error: true,
          errorMsg,
        },
      });
    }
  }

  canInviteToProject(project: PartialProject) {
    // TODO this is a hacky way of checking but will work until custom-roles
    return [...(this.state.orgRoles ?? []), ...(this.getCurrentOrgProjectData()[project.id] ?? [])]
      .map((role) => role.name)
      .includes(`admin`);
  }
}

export const InviteUsersModalEl = defineAndMakeJsxFactory<InviteUsersModalJsxProps>(
  `mp-invite-users-modal-v2`,
  InviteUsersModal,
);
export const InviteUsersModalReact = panel2react<InviteUsersModalJsxProps>(
  `mp-invite-users-modal-v2`,
  InviteUsersModal,
);
